import React, { useCallback, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FormattedMessagePropType } from '../../../prop-types/intl';

import { TooltipPosition } from '../../prop-types/tooltip-position';
import { useArrowClass } from '../../hooks/tooltip/use-arrow-class';
import { usePositioning } from '../../hooks/tooltip/use-positioning';

import { TooltipContent } from './tooltip-content';
import { tooltipChildValidator } from './tooltip-child-validator';

const tooltipContentType = (<TooltipContent />).type;

const TooltipContentChild = React.forwardRef(({ children, containerId, onMouseLeave, position, text }, ref) => {
  const { position: actualPosition, ref: tooltipRef, style } = usePositioning(ref, position);

  const arrowClass = useArrowClass(actualPosition);

  const tooltipContentChild = React.Children.toArray(children).find((child) => child.type === tooltipContentType);

  const tooltipContentProps = {
    arrowClass,
    onMouseLeave,
    position: actualPosition,
    ref: tooltipRef,
    style,
  };

  const tooltipContent = tooltipContentChild ? (
    React.cloneElement(tooltipContentChild, tooltipContentProps)
  ) : (
    <TooltipContent {...tooltipContentProps}>{text}</TooltipContent>
  );

  return ReactDOM.createPortal(tooltipContent, global.document.getElementById(containerId));
});

const createTooltipRootIfDoesNotExist = (containerId) => {
  if (!global.document.getElementById(containerId)) {
    const tooltipWrapper = global.document.createElement('div');

    tooltipWrapper.setAttribute('class', 'tooltip-top-level-wrapper');
    tooltipWrapper.setAttribute('id', containerId);
    tooltipWrapper.setAttribute('style', 'position:relative;');
    tooltipWrapper.setAttribute('role', 'tooltip');

    global.document.body.appendChild(tooltipWrapper);
  }
};

export const Tooltip = ({
  children,
  containerId = 'tooltip-wrapper-container',
  position = 'top',
  text = '',
  shouldHideTooltip = false,
  openDelayMs = 0,
}) => {
  createTooltipRootIfDoesNotExist(containerId);

  const ref = useRef();
  const [hasHover, setHasHover] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!hasHover) {
      setIsOpen(false);
    } else if (openDelayMs <= 0) {
      setIsOpen(true);
    } else {
      const delayTimer = setTimeout(() => {
        setIsOpen(true);
      }, openDelayMs);
      return () => {
        clearTimeout(delayTimer);
      };
    }
  }, [openDelayMs, hasHover]);

  const otherElement = React.Children.toArray(children).find((child) => child.type !== tooltipContentType);

  const onMouseEnter = useCallback(
    (event) => {
      setHasHover(true);
      if (otherElement.props.onMouseEnter) {
        otherElement.props.onMouseEnter(event);
      }
    },
    [otherElement],
  );

  const onMouseLeave = useCallback(
    (event) => {
      setHasHover(false);
      if (otherElement.props.onMouseLeave) {
        otherElement.props.onMouseLeave(event);
      }
    },
    [otherElement],
  );

  if (shouldHideTooltip) {
    return React.cloneElement(otherElement, {
      ...otherElement.props,
      ref,
    });
  }

  return (
    <React.Fragment>
      {React.cloneElement(otherElement, {
        onMouseEnter,
        onMouseLeave,
        ref,
      })}
      {isOpen && (
        <TooltipContentChild ref={ref} containerId={containerId} onMouseLeave={onMouseLeave} position={position} text={text}>
          {children}
        </TooltipContentChild>
      )}
    </React.Fragment>
  );
};

Tooltip.propTypes = {
  children: PropTypes.oneOfType([tooltipChildValidator]).isRequired,
  containerId: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, FormattedMessagePropType]),
  position: TooltipPosition,
  shouldHideTooltip: PropTypes.bool,
  openDelayMs: PropTypes.number,
};
